import { RedTeamTaskIcon } from '@/components/RedTeamTaskIcon'
import './new-run.css'
import { Picker } from '@/components/Picker'
import { FormEvent, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api, ApiError } from '@/api/client'
import { packageLabels, PackageBadges } from '@/components/runs/PackageBadges'
import type { PackageVersion } from '@/lib/task-runs'
import type { Estimate, RunConfiguration, RunRecord } from '@/lib/runs'

interface ExecutionProfile {
  framework: RunConfiguration['framework']; model_patterns: string[]; harness: string; sampler: string
}
interface ConfigResponse {
  schema_version: 2
  enabled: boolean
  programs: { id: string; frameworks: string[]; available: boolean; reason?: string }[]
  harnesses: { id: string; label: string; available?: boolean; sampler?: string }[]
  execution_profiles: ExecutionProfile[]
  frameworks: { id: string; default_harness: string }[]
  default_framework: 'osworld2'
  limits: { parallel: Record<string, number> }
  cloud?: { region: 'us-east-2'; availability_zones: string[] }
}
interface CatalogTask { task_id: string; title: string; program: string; revision: string; splits: string[]; workflow_id?: string; workflow_title?: string; duplicate?: boolean }
interface CatalogPage {
  tasks: CatalogTask[]; total: number; next_offset: number | null; snapshot: string; revisions: Record<string, string>
  sources: Record<string, { source: string; complete: boolean }>; errors: Record<string, string>; workflows: { id: string; title: string }[]
}
/** A chosen task keeps its program and dataset revision, whichever page it was picked from. */
type ChosenTask = CatalogTask

const matchesModel = (profile: ExecutionProfile, input: string) => {
  const model = input.trim() === 'mockmodel' ? 'fake/script' : input
  return profile.model_patterns.some(pattern => pattern.endsWith('*') ? model.startsWith(pattern.slice(0, -1)) : model === pattern)
}

const initial: RunConfiguration = {
  name: '', framework: 'osworld2', tasks: [],
  models: ['qwen/qwen3-vl-8b-instruct'], packages: {}, model: 'qwen/qwen3-vl-8b-instruct', harness: 'promptagent-fixed',
  rollouts: 1, max_steps: 60, seed: 42, seed_strategy: 'increment',
}
const money = (range: number[] | null) => range ? `$${range[0].toFixed(2)}–$${range[1].toFixed(2)}` : 'Unknown'

function IntegerField({ label, value, min = 1, max, onChange }: {
  label: string; value: number; min?: number; max?: number; onChange: (value: number) => void
}) {
  const [draft, setDraft] = useState(String(value))
  useEffect(() => setDraft(String(value)), [value])

  const valid = (raw: string) => {
    const parsed = Number(raw)
    return raw !== '' && Number.isInteger(parsed) && parsed >= min && (max === undefined || parsed <= max)
  }

  return <label className="field"><span>{label}</span><input
    type="text"
    inputMode="numeric"
    pattern="[0-9]*"
    value={draft}
    onChange={event => {
      const raw = event.target.value
      setDraft(raw)
      if (valid(raw)) onChange(Number(raw))
    }}
    onBlur={() => { if (!valid(draft)) setDraft(String(value)) }}
  /></label>
}

export default function NewRun() {
  const navigate = useNavigate()
  const [meta, setMeta] = useState<ConfigResponse>()
  const [catalog, setCatalog] = useState<CatalogPage>()
  const [chosen, setChosen] = useState<Record<string, ChosenTask>>({})
  const [catalogLoading, setCatalogLoading] = useState(false)
  const [requestedLoading, setRequestedLoading] = useState(false)
  const [requestedError, setRequestedError] = useState('')
  const catalogRequest = useRef(0)
  const [catalogErrors, setCatalogErrors] = useState<Record<string, string>>({})
  const [catalogAttempt, setCatalogAttempt] = useState(0)
  const initialTasksApplied = useRef(false)
  const selectionEdits = useRef(new Set<string>())
  const [params] = useSearchParams()
  const requestedProgram = params.get('program') ?? ''
  const requestedRevision = params.get('revision') ?? ''
  const requestedTasks = params.getAll('task')
  const [form, setForm] = useState<RunConfiguration>(() => ({ ...initial,
    tasks: requestedTasks, name: params.get('name') ?? '' }))
  const [estimate, setEstimate] = useState<Estimate>()
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [packageOptions, setPackageOptions] = useState<Record<string, PackageVersion[]>>({})
  const [packageErrors, setPackageErrors] = useState<Record<string, string>>({})
  const models = form.models ?? [form.model]
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [programFilter, setProgramFilter] = useState('')
  const [workflowFilter, setWorkflowFilter] = useState('')
  const [selectedOnly, setSelectedOnly] = useState(false)
  const programs = (meta?.programs ?? []).filter(program => program.available && (!requestedRevision || !requestedProgram || program.id === requestedProgram)).map(program => program.id)
  const programKey = programs.join(',')
  const pinned = (program: string) => requestedRevision && program === requestedProgram ? { revision: requestedRevision } : {}
  const packageChoice = (taskId: string) => form.packages?.[taskId] ?? (requestedRevision && chosen[taskId]?.program === requestedProgram ? 'dataset' : 'latest')
  const revisionFor = (taskId: string) => chosen[taskId]?.revision ?? ''
  const fetchCatalog = (offset: number, extra: Record<string, string> = {}) => api.get<CatalogPage>('/eng/runs/tasks', {
    program: programKey, ...(programs.length === 1 ? pinned(programs[0]) : {}), offset, ...extra,
  })


  useEffect(() => {
    api.get<ConfigResponse>('/eng/runs/config').then(value => {
      setMeta(value)
      setForm(old => {
        const allowed = value.execution_profiles.filter(profile => profile.framework === old.framework && matchesModel(profile, old.model))
          .map(profile => profile.harness)
        return { ...old, harness: allowed.includes(old.harness) ? old.harness : allowed[0] ?? '' }
      })
    }).catch(e => setError(e.message))
  }, [])
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search.trim()), 250)
    return () => window.clearTimeout(timer)
  }, [search])
  // One page across every program; search and filters run on the server.
  useEffect(() => {
    if (!programKey) return
    const request = ++catalogRequest.current
    setCatalogLoading(true)
    setCatalogErrors({})
    fetchCatalog(0, { q: query, filter_program: programFilter, workflow: workflowFilter }).then(page => {
      if (request !== catalogRequest.current) return
      setCatalog(page)
      setCatalogErrors(page.errors)
    }).catch(e => {
      if (request === catalogRequest.current) setCatalogErrors(Object.fromEntries(programs.map(program => [program, (e as Error).message])))
    }).finally(() => { if (request === catalogRequest.current) setCatalogLoading(false) })
  }, [programKey, query, programFilter, workflowFilter, catalogAttempt])
  async function loadMore() {
    if (catalog?.next_offset == null) return
    const request = catalogRequest.current
    setCatalogLoading(true)
    try {
      const page = await fetchCatalog(catalog.next_offset, { q: query, filter_program: programFilter, workflow: workflowFilter, snapshot: catalog.snapshot })
      if (request !== catalogRequest.current) return
      setCatalog(old => old && { ...page, tasks: [...old.tasks, ...page.tasks] })
      setCatalogErrors(page.errors)
    } catch (e) {
      if (request !== catalogRequest.current) return
      if (e instanceof ApiError && e.status === 409) {
        setCatalog(undefined)
        setCatalogAttempt(value => value + 1)
        setError('The task list changed and is being reloaded. Your selected packages have been kept.')
      } else setError((e as Error).message)
    } finally { if (request === catalogRequest.current) setCatalogLoading(false) }
  }
  // Tasks named in the link are looked up by ID, not searched for in loaded pages.
  useEffect(() => {
    if (!programKey || initialTasksApplied.current) return
    if (!requestedTasks.length) { initialTasksApplied.current = true; return }
    let cancelled = false
    setRequestedLoading(true)
    setRequestedError('')
    const found: ChosenTask[] = []
    const collect = async (offset: number, snapshot?: string): Promise<void> => {
      const page = await fetchCatalog(offset, { tasks: requestedTasks.join(','), ...(snapshot ? { snapshot } : {}) })
      if (cancelled) return
      if (Object.keys(page.errors).length) throw new Error('Some linked tasks could not be loaded. Try again.')
      found.push(...page.tasks)
      if (page.next_offset != null) return collect(page.next_offset, page.snapshot)
    }
    collect(0).then(() => {
      if (cancelled) return
      initialTasksApplied.current = true
      // A late lookup may initialize untouched links, but must not undo the user's
      // task or package choices made while the ordinary catalogue was already usable.
      const untouched = requestedTasks.filter(id => !selectionEdits.current.has(id))
      if (!untouched.length) return
      const resolved = found.filter(task => untouched.includes(task.task_id))
      const tasks = untouched.filter(id => resolved.some(task => task.task_id === id))
      setChosen(old => ({ ...old, ...Object.fromEntries(resolved.map(task => [task.task_id, task])) }))
      setForm(old => ({ ...old, tasks: [...new Set([...old.tasks.filter(id => !untouched.includes(id)), ...tasks])],
        packages: { ...Object.fromEntries(requestedRevision ? tasks.map(id => [id, 'dataset']) : []), ...old.packages } }))
      setEstimate(undefined)
      if (tasks.length !== untouched.length) setError('Some selected tasks are absent from the pinned package. Check the task selection before launching.')
    }).catch(e => { if (!cancelled) setRequestedError((e as Error).message) })
      .finally(() => { if (!cancelled) setRequestedLoading(false) })
    return () => { cancelled = true }
  }, [programKey, catalogAttempt])

  const patch = (value: Partial<RunConfiguration>) => {
    setForm(old => ({ ...old, ...value, ...(value.tasks ? { packages: Object.fromEntries(Object.entries(old.packages ?? {}).filter(([id]) => value.tasks!.includes(id))) } : {}) }))
    setEstimate(undefined)
  }
  const profilesFor = (framework = form.framework, selected = models) =>
    (meta?.execution_profiles ?? []).filter(profile => profile.framework === framework && selected.every(model => matchesModel(profile, model)))
  const selectModels = (next: string[]) => {
    const allowed = profilesFor(form.framework, next).map(profile => profile.harness)
    patch({ models: next, model: next[0] ?? '', harness: allowed.includes(form.harness) ? form.harness : allowed[0] ?? '' })
  }
  const selectFramework = (framework: RunConfiguration['framework']) => {
    const allowed = profilesFor(framework).map(profile => profile.harness)
    patch({ framework, harness: allowed.includes(form.harness) ? form.harness : allowed[0] ?? '' })
  }
  useEffect(() => {
    let cancelled = false
    for (const taskId of form.tasks) {
      if (packageOptions[taskId] !== undefined || packageErrors[taskId]) continue
      api.get<{ available_packages: PackageVersion[] }>(`/eng/runs/task-board/${encodeURIComponent(taskId)}`)
        .then(value => { if (!cancelled) setPackageOptions(old => ({ ...old, [taskId]: value.available_packages })) })
        .catch(ex => { if (!cancelled) {
          if (ex instanceof ApiError && ex.status === 404) setPackageOptions(old => ({ ...old, [taskId]: [] }))
          else setPackageErrors(old => ({ ...old, [taskId]: ex.message }))
        } })
    }
    return () => { cancelled = true }
  }, [form.tasks, packageOptions, packageErrors])
  async function calculate(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try { setEstimate(await api.post<Estimate>('/eng/runs/estimates', {
      ...form,
      packages: Object.fromEntries(form.tasks.map(id => [id, packageChoice(id)])),
      task_selection: form.tasks.map(task_id => ({ task_id,
        program: chosen[task_id].program, revision: revisionFor(task_id) })),
    })) }
    catch (ex) { setError(ex instanceof ApiError ? ex.message : (ex as Error).message) }
    finally { setBusy(false) }
  }
  async function launch() {
    if (!estimate) return
    setBusy(true)
    setError('')
    try {
      const run = await api.post<RunRecord>('/eng/runs', {
        estimate_id: estimate.estimate_id,
        confirmed: true,
        acknowledge_low_confidence: estimate.confidence === 'low',
        acknowledge_unknown_model_cost: estimate.model_cost_unknown,
      })
      navigate(`/eng/runs/${run.run_id}`)
    } catch (ex) {
      setError(ex instanceof ApiError ? ex.message : (ex as Error).message)
      setBusy(false)
    }
  }
  const missingTasks = form.tasks.some(id => !chosen[id])
  const selectedPrograms = [...new Set(form.tasks.flatMap(id => chosen[id] ? [chosen[id].program] : []))]
  const incompatible = selectedPrograms.some(id => !meta?.programs.find(program => program.id === id)?.frameworks.includes(form.framework))
  const workflows = (catalog?.workflows ?? []).map(({ id, title }) => [id, title] as const)
  const choose = (task: CatalogTask, on: boolean) => {
    selectionEdits.current.add(task.task_id)
    if (on) setChosen(old => ({ ...old, [task.task_id]: task }))
    patch({ tasks: on ? [...form.tasks, task.task_id] : form.tasks.filter(id => id !== task.task_id) })
  }
  // The selection is already loaded, so "Selected only" filters it here.
  const visibleTasks = selectedOnly ? form.tasks.flatMap(id => chosen[id] ? [chosen[id]] : []).filter(task =>
    (!programFilter || task.program === programFilter) && (!workflowFilter || task.workflow_id === workflowFilter)
    && `${task.title} ${task.task_id} ${task.workflow_title ?? ''}`.toLowerCase().includes(query.toLowerCase())) : catalog?.tasks ?? []

  return <div className="narrow">
    <button className="link backlink" onClick={() => navigate('/eng/runs')}>← Runs</button>
    <div className="page-head"><div><h1>Run tasks</h1><div className="meta">Choose tasks and packages and review the expected cost before launch.</div></div></div>
    {error && <div className="note err" role="alert">{error}</div>}
    <form onSubmit={calculate} className="run-form"><fieldset className="run-form-fields" disabled={busy}>
      <label className="run-name-field">
        <input aria-label="Experiment name" type="text" required minLength={3} maxLength={80} placeholder="Untitled experiment" value={form.name} onChange={event => patch({ name: event.target.value })} />
      </label>
      <section className="form-section run-task-section">
        <div className="run-task-heading"><h2>Tasks</h2><span className="muted small" role="status">{form.tasks.length} selected</span></div>
        <div className="run-task-filters">
          <input type="search" aria-label="Search tasks" placeholder="Search tasks…" value={search} onChange={event => setSearch(event.target.value)} />
          <Picker label="Filter by program" value={programFilter} onChange={setProgramFilter} options={[
            { value: '', label: 'All programs' }, ...programs.map(id => ({ value: id, label: id })),
          ]} />
          {workflows.length > 0 && <Picker label="Filter by workflow" value={workflowFilter} onChange={setWorkflowFilter} options={[
            { value: '', label: 'All workflows' }, ...workflows.map(([id, title]) => ({ value: id, label: title })),
          ]} />}
          <label className="inline-check"><input type="checkbox" checked={selectedOnly} onChange={event => setSelectedOnly(event.target.checked)} />Selected only</label>
        </div>
        {(!!Object.keys(catalogErrors).length || requestedError) && <div className="note warn" role="alert">
          {!!Object.keys(catalogErrors).length && <span>Could not load tasks for {Object.keys(catalogErrors).join(', ')}. You can select tasks from programs that loaded. </span>}
          {requestedError && <span>Could not load linked tasks: {requestedError} </span>}
          <button type="button" disabled={catalogLoading || requestedLoading} onClick={() => setCatalogAttempt(value => value + 1)}>Retry loading tasks</button>
        </div>}
        {Object.entries(catalog?.sources ?? {}).filter(([, scope]) => scope.source === 'imported_materials').map(([program]) => <p className="note" key={program}>Showing imported {program} tasks from the pinned commit. Other Hugging Face tasks may not appear.</p>)}
        {!requestedLoading && missingTasks && <p className="note err">Some selected tasks are unavailable. Reload the task list before continuing.</p>}
        {catalogLoading && <p className="muted" role="status">Loading tasks…</p>}
        {requestedLoading && <p className="muted" role="status">Loading selected tasks…</p>}
        <div className="task-picker run-task-picker">{visibleTasks.map(task => <label className="inline-check" key={`${task.program}:${task.task_id}`}>
          <input type="checkbox" disabled={busy || task.duplicate} checked={form.tasks.includes(task.task_id)} onChange={event => choose(task, event.target.checked)} />
          <span><strong><RedTeamTaskIcon task={task} />{task.title}</strong><small>{task.task_id}{task.workflow_title ? ` · ${task.workflow_title}` : ''}{task.duplicate ? ' · Duplicate task ID; unavailable' : ''}</small></span>
          <small className="run-task-program">{task.program}</small>
        </label>)}</div>
        {!catalogLoading && visibleTasks.length === 0 && <p className="muted">No tasks match these filters.</p>}
        {!selectedOnly && catalog && catalog.total > 0 && <div className="run-task-more">
          <span className="muted small">Showing {catalog.tasks.length} of {catalog.total}</span>
          {catalog.next_offset != null && <button type="button" className="btn" disabled={catalogLoading} onClick={loadMore}>{catalogLoading ? 'Loading…' : 'Show more tasks'}</button>}
        </div>}
      </section>
      <section className="form-section run-setup-section">
        <h2>Run settings</h2>
        <div className="grid grid-2 run-settings-fields"><IntegerField label="Rollouts per task, per model" value={form.rollouts} onChange={value => patch({ rollouts: value })} /><IntegerField label="Maximum steps per rollout, per model" value={form.max_steps} onChange={value => patch({ max_steps: value })} /></div>
        <div className="grid grid-2 run-foundation-fields">
          <label className="field"><span>Framework</span><Picker label="Framework" field value={form.framework} onChange={value => selectFramework(value as RunConfiguration['framework'])} options={[{ value: 'osworld2', label: 'OSWorld2' }, { value: 'gymanything', label: 'GymAnything' }]} /></label>
        </div>
        {incompatible && <p className="note warn">The selected framework cannot run all selected tasks. Choose a compatible framework or remove those tasks.</p>}
        <div className="run-model-harness-grid">
          <div className="field run-model-field"><span className="field-label">Models</span>
            <span id="run-model-hint" className="hint">Enter <code>mockmodel</code> for scripted fake actions up to the requested maximum steps (no model charges; EC2 still billed). Each model runs every selected task.</span>
            {models.map((model, index) => <div className="run-model-row" key={index}>
              <input aria-label={`Model ID ${index + 1}`} type="text" required pattern="\S+" placeholder="provider/model" value={model} onChange={event => selectModels(models.map((m, i) => i === index ? event.target.value : m))} />
              {models.length > 1 && <button type="button" className="btn" aria-label={`Remove model ${index + 1}`} onClick={() => selectModels(models.filter((_, i) => i !== index))}>Remove</button>}
            </div>)}
            <button type="button" className="btn" disabled={models.length >= 20} onClick={() => selectModels([...models, ''])}>Add model</button>
          </div>
          <div className="field run-harness-field"><span className="field-label">Harness</span>
            <Picker label="Harness" field value={form.harness} onChange={harness => patch({ harness })} options={(meta?.harnesses ?? []).map(harness => {
              const available = profilesFor().some(profile => profile.harness === harness.id)
              return { value: harness.id, label: harness.label, disabled: !available, detail: available ? undefined : 'Unavailable' }
            })} />
          </div>
        </div>
      </section>
      {form.tasks.length > 0 && <section className="form-section"><h2>Packages</h2>
        <p className="job-equation"><strong>{form.tasks.length}</strong> {form.tasks.length === 1 ? 'task' : 'tasks'} × <strong>{form.rollouts}</strong> {form.rollouts === 1 ? 'rollout' : 'rollouts'} per task = <strong>{form.tasks.length * form.rollouts}</strong> {form.tasks.length * form.rollouts === 1 ? 'job' : 'jobs'} per model × <strong>{models.length}</strong> {models.length === 1 ? 'model' : 'models'} = <strong>{form.tasks.length * form.rollouts * models.length}</strong> total jobs.</p>
        <p className="hint">REMI schedules jobs on compatible shared pool slots and manages hardware allocation.</p>
        <div className="package-selectors"><p className="hint">Latest is resolved and pinned when you calculate the estimate. Package history includes work in progress and agent submissions.</p>
          {form.tasks.map(taskId => { const versions = packageOptions[taskId] ?? []; const choice = packageChoice(taskId); const selected = choice === 'latest' ? versions.find(v => v.latest) : versions.find(v => v.package_key === choice)
            return <div className="field" key={taskId}><span>{chosen[taskId]?.title ?? taskId}</span>
              <Picker label={`Package for ${taskId}`} field value={choice} onChange={value => patch({ packages: { ...form.packages, [taskId]: value } })} options={[
                { value: 'dataset', label: 'Pinned HF commit', detail: revisionFor(taskId) },
                { value: 'latest', label: 'Latest', detail: 'The task\'s current package (set by hand or by authoring), or default dataset package' },
                ...versions.filter(v => v.package_key && v.reference?.commit).map(v => ({ value: v.package_key!, label: `${v.reference!.commit!.slice(0, 10)} · ${packageLabels(v).join(' · ') || 'Historical package'}` }))
              ]} />{selected && <PackageBadges version={selected} />}{packageErrors[taskId] && <span className="note err">{packageErrors[taskId]}</span>}
            </div> })}
        </div>
      </section>}
      <button className="primary" disabled={busy || missingTasks || incompatible || !programs.length || form.tasks.some(id => chosen[id]?.duplicate) || !form.tasks.length || models.some(m => !m.trim()) || new Set(models.map(m => m.trim() === 'mockmodel' ? `fake/script:steps=${form.max_steps}` : m.trim())).size !== models.length || !form.harness}>{busy ? 'Calculating…' : 'Review cost estimate'}</button>
    </fieldset></form>
    {estimate && <section className="box run-estimate"><h2>Confirm experiment</h2><div className="estimate-grid"><div><span>Estimated model cost</span><strong>{money(estimate.model_cost_usd)}</strong></div>{!estimate.ec2_cost_unknown && <><div><span>EC2</span><strong>{money(estimate.ec2_cost_usd)}</strong></div><div><span>Total</span><strong>{money(estimate.total_cost_usd)}</strong></div></>}</div>{estimate.per_model && <table><thead><tr><th>Model</th><th>Jobs per model</th><th>Model cost</th>{!estimate.ec2_cost_unknown && <><th>EC2 per model</th><th>Total per model</th></>}</tr></thead><tbody>{estimate.per_model.map(value => <tr key={value.model}><td>{value.model}</td><td>{estimate.jobs_per_model}</td><td>{money(value.model_cost_usd)}</td>{!estimate.ec2_cost_unknown && <><td>{money(value.ec2_cost_usd)}</td><td>{money(value.total_cost_usd)}</td></>}</tr>)}</tbody></table>}<p className="small muted">{estimate.sample_count} comparable runs · {estimate.confidence} confidence · {estimate.basis}</p>{estimate.confidence === 'low' && <div className="note warn">Historical data is limited. Conservative bounds are shown.</div>}{estimate.ec2_cost_unknown && <p className="hint">REMI manages compute allocation. EC2 cost is not estimated here and is billed separately.</p>}{estimate.model_cost_unknown && <div className="note warn">OpenRouter did not provide usable per-token pricing. Model cost is unknown.</div>}{estimate.pricing_error && <p className="note warn">Pricing refresh failed ({estimate.pricing_error}).</p>}{estimate.pricing_fetched_at && <p className="small muted">OpenRouter rates fetched {new Date(estimate.pricing_fetched_at).toLocaleString()}{estimate.pricing_stale ? ' · refresh failed; using the last snapshot (under 24 hours old)' : ''}.</p>}<ul className="small muted">{estimate.assumptions.map(assumption => <li key={assumption}>{assumption}</li>)}</ul><button className="primary" disabled={busy} onClick={launch}>{busy ? 'Launching…' : 'Launch experiment'}</button></section>}
  </div>
}
